import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';

const InvoiceModal = ({ show, onClose, registration }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const [totalAmount, setTotalAmount] = useState(0);

  const formatNumber = (number, locale) => {
    if (isNaN(number)) {
      return '0.00';
    }
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateString, locale) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString(locale, options).toUpperCase();
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hr')}`;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const calculateTotalAmount = () => {
    const classPrice = parseFloat(registration.price) || 0;
    const inventoryTotal = (registration.inventory_items || []).reduce(
      (sum, item) => {
        const parsedItem = JSON.parse(item);
        return sum + parseFloat(parsedItem.price_per_unit || 0);
      },
      0
    );
    return classPrice + inventoryTotal;
  };

  useEffect(() => {
    if (show && registration) {
      setTotalAmount(calculateTotalAmount());
    }
  }, [show, registration]);

  if (!show) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            zIndex: 10,
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>

        <div ref={componentRef} className="invoice-print-content" style={{ padding: '32px' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '32px',
            paddingBottom: '20px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <img
                src="/images/Theplanner.png"
                alt="The Planner Education"
                style={{ width: '80px', height: 'auto' }}
              />
              <div style={{ fontSize: '11px', lineHeight: '1.4', color: '#4b5563' }}>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '4px' }}>
                  THE PLANNER EDUCATION
                </div>
                <div>The Mercury Ville @Chidlom 3rd Floor</div>
                <div>Lumpini Pathumwan Bangkok</div>
                <div>www.theplannereducation.com</div>
                <div>Tel. 02-253-2533 or 095-726-2666</div>
                <div>Line: @theplanner</div>
              </div>
            </div>

            <div style={{ textAlign: 'right', fontSize: '12px' }}>
              <h2 style={{
                margin: '0 0 12px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: registration.is_refunded ? '#dc2626' : '#1f2937'
              }}>
                {registration.is_refunded ? t('creditNote') : t('invoice')}
              </h2>
              <div style={{ color: '#6b7280', lineHeight: '1.5' }}>
                <div><strong>{t('invoiceNo')}:</strong> {registration.invoiceno}</div>
                <div><strong>{t('date')}:</strong> {formatDate(registration.date, i18n.language)}</div>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '24px',
            fontSize: '12px',
            lineHeight: '1.5'
          }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              {t('billTo')}:
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px' , gap: '8px 16px', color: '#4b5563' }}>
              <div><strong>{t('name')}:</strong> {registration.student_name}</div>
              <div><strong>{t('phone')}:</strong> {registration.phone || t('na')}</div>
              <div><strong>{t('class')}:</strong> {registration.class_code}</div>
              <div><strong>{t('hours')}:</strong> {formatMinutesToHours(registration.total_hours)}</div>
            </div>
            {registration.address && (
              <div style={{ marginTop: '8px', color: '#4b5563' }}>
                <strong>{t('address')}:</strong> {registration.address}
              </div>
            )}
          </div>

        {/* Items List */}
          <div style={{ marginBottom: '24px' }}>
          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 60px 100px' ,
            gap: '16px',
            padding: '12px 16px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderBottom: 'none',
            fontSize: '12px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            <div>{t('description')}</div>
            <div style={{ textAlign: 'center' }}>{t('quantity')}</div>
            <div style={{ textAlign: 'right' }}>{t('amount')}</div>
          </div>

          {/* Class Item */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 60px 100px' ,
            gap: '16px',
            padding: '10px 16px',
            borderLeft: '1px solid #e5e7eb',
            borderRight: '1px solid #e5e7eb',
            borderBottom: '1px solid #f3f4f6',
            fontSize: '12px',
            color: '#4b5563'
          }}>
            <div>{registration.class_code} {formatMinutesToHours(registration.total_hours)}</div>
            <div style={{ textAlign: 'center' }}>1</div>
            <div style={{ textAlign: 'right' }}>{formatNumber(registration.price, i18n.language)}</div>
          </div>

          {/* Inventory Items */}
          {registration.inventory_items &&
            registration.inventory_items.map((item, index) => {
              const parsedItem = JSON.parse(item);
              return (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 60px 100px' ,
                  gap: '16px',
                  padding: '10px 16px',
                  borderLeft: '1px solid #e5e7eb',
                  borderRight: '1px solid #e5e7eb',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '12px',
                  color: '#4b5563'
                }}>
                  <div>{parsedItem.label}</div>
                  <div style={{ textAlign: 'center' }}>1</div>
                  <div style={{ textAlign: 'right' }}>{formatNumber(parsedItem.price_per_unit, i18n.language)}</div>
                </div>
              );
            })
          }
          
          {/* Bottom border */}
          <div style={{
            borderLeft: '1px solid #e5e7eb',
            borderRight: '1px solid #e5e7eb',
            borderBottom: '1px solid #e5e7eb',
            height: '1px'
          }}></div>
          </div>

          {/* Summary */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '24px'
          }}>
            <div style={{ width: '280px', fontSize: '12px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f3f4f6',
                color: '#4b5563'
              }}>
                <span>{t('total')}:</span>
                <span>{formatNumber(totalAmount, i18n.language)} THB</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f3f4f6',
                color: '#4b5563'
              }}>
                <span>{t('discount')}:</span>
                <span>{formatNumber(-registration.discount, i18n.language)} THB</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f3f4f6',
                color: '#4b5563'
              }}>
                <span>{t('VAT 7%', { percentage: 7 })}:</span>
                <span>{formatNumber(registration.vat, i18n.language)} THB</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0 8px 0',
                borderTop: '2px solid #e5e7eb',
                fontWeight: '600',
                fontSize: '14px',
                color: '#1f2937'
              }}>
                <span>{t('grandTotal')}:</span>
                <span>{formatNumber(registration.final_price, i18n.language)} THB</span>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '6px',
            fontSize: '12px',
            lineHeight: '1.6',
            color: '#4b5563',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px' , gap: '4px 16px' }}>
              <div><strong>{t('paymentMethod')}:</strong> {registration.payment_method}</div>
              <div><strong>{t('memo')}:</strong> {registration.memo || t('na')}</div>
            </div>
            <div style={{
              marginTop: '8px',
              fontWeight: '500',
              color: '#059669'
            }}>
              {t('paymentConfirmed')}
            </div>
          </div>

          {/* Signature */}
          {registration.signature && (
            <div style={{
              marginBottom: '24px',
              fontSize: '12px'
            }}>
              <div style={{
                color: '#4b5563',
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                {t('signature')}:
              </div>
              <img
                src={registration.signature}
                alt="Signature"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {/* Print Button */}
          <div className="no-print" style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <button
              onClick={handlePrint}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              {t('printInvoice')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;